import type { ListingDocumentType } from '@bb/domain/dist/types';
import { FindDTO as ListingType } from '@bb/domain/dist/types';

export enum PageView {
  Documents,
  Details,
  Visibility,
}

// Import this type to create Props
export type CreateListingCardProps<T extends keyof ListingFormState> = Pick<
  ListingFormState,
  T | 'type' | 'subtype'
> & {
  setListing?: SetListingType;
};

// Sub-components:
export type ListingDetailsFields =
  | 'type'
  | 'subtype'
  | 'line1'
  | 'line2'
  | 'country'
  | 'city'
  | 'province'
  | 'neighborhood'
  | 'postal_code'
  | 'list_price'
  | 'list_price_type'
  | 'expiry'
  | 'commission'
  | 'status'
  | 'coming_soon_date'
  | 'reps';

export type VirtualTourFields = 'virtualTourUrl';

export type DescriptionFields = 'client_remarks' | 'brokerage_remarks';

export type LocationFields =
  | 'subtype'
  | 'dir_cross'
  | 'fronts_on'
  | 'taxes'
  | 'possession'
  | 'occupancy'
  | 'holdover'
  | 'legal_description';

export type OpenHousesFields = 'openHouses' | 'timezone';

export type ExteriorFields =
  | 'type'
  | 'subtype'
  | 'exteriorType'
  | 'style'
  | 'exterior'
  | 'lot_front'
  | 'lot_depth'
  | 'garage_type'
  | 'garage_spaces'
  | 'drive'
  | 'parking_spaces'
  | 'parking_cost'
  | 'parking_included'
  | 'private_entrance';

export type PropertyDetailsFields =
  | 'total_rooms'
  | 'bedrooms'
  | 'bathrooms'
  | 'rooms_plus'
  | 'bedrooms_plus'
  | 'kitchens'
  | 'basement'
  | 'laundry_access'
  | 'heat_source'
  | 'heat_type'
  | 'ac'
  | 'age'
  | 'apx_sqft'
  | 'sqft'
  | 'water'
  | 'utilities';

export type RoomsFields = 'rooms';

export type RequiredFields = 'required';

export type IncludedExcludedFields = 'freehold';

export type CondoDetailsFields =
  | 'condo'
  | 'exterior'
  | 'garage_type'
  | 'garage_spaces';

export type MoreInfoFields = 'more_info';

export type FinancialsFields = 'financials';

export type SmallPreviewFields =
  | 'thumbnail'
  | 'line1'
  | 'line2'
  | 'city'
  | 'province'
  | 'postal_code'
  | 'list_price';

export type VisibilityFields =
  | SmallPreviewFields
  | 'visibility'
  | 'pendingVisibility'
  | 'pageStatus'
  | 'status';

export type StatusPriceChangeFields =
  | SmallPreviewFields
  | 'list_price'
  | 'availability';

// Other types:

export type OpenHouse = Omit<
  ListingType['openHouses'][number],
  'date' | 'timeTo' | 'timeFrom'
> & {
  date: Date;
  timeTo: Date;
  timeFrom: Date;
};

export type PhotoFileStatus = 'error' | 'done' | 'uploading' | 'removed';

export type PhotoFileType = {
  id?: string;
  url: string;
  mimetype: string;
  filename: string;
  isDeleted?: boolean;
  size?: number;
  updatedAt?: Date | string;
  // Used only for FE
  status: PhotoFileStatus;
  percent?: number;
  order?: number;
};

export type AttachmentsType = (PhotoFileType & { uid: string })[];

export type UpdateFileType = (fileId: string, file: PhotoFileType) => void;

export type AvailabilityDetailsType = {
  newAvailability?:
    | 'Price Change'
    | 'Sold'
    | 'Terminated'
    | 'Pending'
    | 'Temp Off Market'
    | 'Suspended'
    | 'Leased'
    | 'Sold Conditionally'
    | 'Leased Conditionally';
  suspended_date?: Date;
  sold_date?: Date;
  closed_date?: Date;
  leased_date?: Date;
  leased_term?: string;
  price?: number;
  coop_brokerage?: string;
  coop_salesperson_1?: string;
  coop_salesperson_2?: string;
  condition?: string;
  condition_expiry?: Date;
};

// Main State:
export type ListingFormState = Pick<
  Partial<ListingType>,
  | 'type'
  | 'subtype'
  | 'org'
  | 'location'
  | 'locations'
  | 'createdBy'
  | 'isDeleted'
  | 'isIncomplete'
  | 'pendingVisibility'
  | 'documentStatus'
  | 'availability'
  | 'country'
  | 'timezone'
  | 'roomUnit'
  | Exclude<ListingDetailsFields, 'expiry' | 'reps' | 'coming_soon_date'>
  | VirtualTourFields
  | DescriptionFields
  | LocationFields
  | ExteriorFields
  | PropertyDetailsFields
  | RoomsFields
  | RequiredFields
  | IncludedExcludedFields
  | CondoDetailsFields
  | MoreInfoFields
  | FinancialsFields
  | VisibilityFields
  | StatusPriceChangeFields
> & {
  id: string;
  openHouses: OpenHouse[];
  expiry?: Date;
  coming_soon_date?: Date;
  reps: {
    id: string;
    firstName: string;
    lastName: string;
    location: string;
    avatarFilestackId?: string;
    team?: string;
  }[];
  canEdit?: boolean;
};

// Main setter:
export type SetListingType = React.Dispatch<
  React.SetStateAction<ListingFormState>
>;

export type SaveListingType = (options?: {
  forceDraft?: boolean;
}) => Promise<boolean | void>;

export interface Document {
  uid: string;
  documentType: ListingDocumentType;
  mimetype: string;
  name: string;
  url: string;
}

export { ListingType };
